import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { createFetchAssignableMembersQueryFn } from 'app/queries/organizations/createFetchAssignableMembersQueryFn';
import { createFetchWorkspaceMembersQueryFn } from 'app/queries/organizations/createFetchWorkspaceMembersQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { createFetchOrganizationTeamsQueryFn } from 'app/queries/teams/createFetchOrganizationTeamsQueryFn';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { createFetchWorkspaceTeamsQueryFn } from 'app/queries/workspaces/createFetchWorkspaceTeamsQueryFn';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';

const SEARCH_DEBOUNCE_MS = 500;
const MEMBERS_PAGE_SIZE = 20;

type TParams = {
  organizationId: string;
  organizationSlug: string;
  workspaceId: string;
  workspaceSlug: string;
  canManageMembers: boolean;
  search: string;
  teamsSlugFilter: string;
};

export const useAssigneeCandidates = ({
  organizationId,
  organizationSlug,
  workspaceId,
  workspaceSlug,
  canManageMembers,
  search,
  teamsSlugFilter,
}: TParams) => {
  const { call } = useAPI();
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const hasSlugs = organizationSlug !== '' && workspaceSlug !== '';

  const organizationMembersQuery = useQuery({
    queryKey: organizationsKeys.assignableMembers(organizationId, {
      pageSize: MEMBERS_PAGE_SIZE,
      orderBy: '',
      orderType: '',
      search: debouncedSearch,
      teamsSlugFilter,
    }),
    queryFn: createFetchAssignableMembersQueryFn(call),
    enabled: canManageMembers && organizationId !== '' && !isSearchSettling,
    placeholderData: keepPreviousData,
  });

  const workspaceMembersQuery = useQuery({
    queryKey: organizationsKeys.workspaceMembers(organizationId, workspaceId, {
      search: debouncedSearch,
      teamsSlugFilter,
    }),
    queryFn: createFetchWorkspaceMembersQueryFn(call),
    enabled: !canManageMembers && organizationId !== '' && workspaceId !== '' && !isSearchSettling,
    placeholderData: keepPreviousData,
  });

  const organizationTeamsQuery = useQuery({
    queryKey: teamsKeys.listsByOrganization(organizationId),
    queryFn: createFetchOrganizationTeamsQueryFn(call),
    enabled: canManageMembers && organizationId !== '' && hasSlugs,
  });

  const workspaceTeamsQuery = useQuery({
    queryKey: workspacesKeys.teamsBySlugs(organizationSlug, workspaceSlug),
    queryFn: createFetchWorkspaceTeamsQueryFn(call),
    enabled: !canManageMembers && hasSlugs,
  });

  useNotifyQueryError(organizationMembersQuery.error);
  useNotifyQueryError(workspaceMembersQuery.error);
  useNotifyQueryError(organizationTeamsQuery.error);
  useNotifyQueryError(workspaceTeamsQuery.error);

  const membersQuery = canManageMembers ? organizationMembersQuery : workspaceMembersQuery;
  const teamsQuery = canManageMembers ? organizationTeamsQuery : workspaceTeamsQuery;

  const users = (canManageMembers ? organizationMembersQuery.data?.data : workspaceMembersQuery.data) ?? [];
  const teams = (canManageMembers ? organizationTeamsQuery.data : workspaceTeamsQuery.data?.result) ?? [];
  const isLoadingData = membersQuery.isLoading || teamsQuery.isLoading;

  return { users, teams, isLoadingData };
};
